import React, { useContext, useState, useEffect } from "react";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import AlertContext from "../../../Context/Alert/AlertContext";
import { BaseURL } from "../../../Files/BaseURL";

const BusinessSummary = () => {
  const { showAlert } = useContext(AlertContext);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("");

  const fetchLatestJobs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("p@token");
      const response = await fetch(`${BaseURL}/api/bussniess/my-latestjobs`, {
        method: "GET",
        headers: {
          "auth-token": token,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch jobs");
      }

      const result = await response.json();
      if (result.success) {
        setJobs(result.jobs);
      } else {
        showAlert(result.message || "API request failed", "danger");
      }
    } catch (err) {
      showAlert(err.message, "danger");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("p@token");
      const url = selectedPeriod
        ? `${BaseURL}/api/bussniess/totalProjectsWithStatusByfilter?period=${selectedPeriod}`
        : `${BaseURL}/api/bussniess/totalProjectsWithStatus`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "auth-token": token,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch project stats");
      }

      const result = await res.json();
      if (!result.success) {
        showAlert(result.message || "API Error", "danger");
      } else {
        setData(result.data);
      }
    } catch (err) {
      showAlert(err.message, "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestJobs();
    fetchProjectStats();
  }, [selectedPeriod]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handlePeriodChange = (e) => {
    setSelectedPeriod(e.target.value);
  };

  const getProgressPercentage = (status) => {
    switch (status) {
      case "New":
        return 0;
      case "Under review":
        return 15;
      case "In progress":
        return 30;
      case "Declining":
        return 45;
      case "Decline Delivery":
        return 60;
      case "Delivered":
        return 80;
      case "Completed":
        return 100;
      case "Declined":
        return 0;
      default:
        return 0;
    }
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "Completed":
        return "bg-primarygreen/30 text-primarygreen";
      case "Delayed":
        return "bg-yellow-500/30 text-yellow-500";
      default:
        return "bg-red-500/30 text-red-500";
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="basis-[65%] bg-white/50 py-4 px-4 rounded-xl">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 font-pop">
          <h2 className="text-lg font-medium">Project Summary</h2>
        </div>
        <div className="relative overflow-x-auto">
          <table className="w-full font-pop text-left rtl:text-right text-gray-500 dark:text-gray-400">
            <thead className="text-sm text-gray-700 uppercase border-b-2 border-black dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-6 py-3">
                  Name
                </th>
                <th scope="col" className="px-6 py-3">
                  Due date
                </th>
                <th scope="col" className="px-6 py-3">
                  Status
                </th>
                <th scope="col" className="px-6 py-3">
                  Progress
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center">
                    Loading...
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center">
                    No jobs found
                  </td>
                </tr>
              ) : (
                jobs.map((job, index) => (
                  <tr
                    key={index}
                    className="bg-white font-pop border-b dark:bg-gray-800 dark:border-gray-700"
                  >
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                    >
                      {job.projectName}
                    </th>
                    <td className="px-6 py-4">{formatDate(job.dueDate)}</td>
                    <td className="px-6 py-4">
                      <div
                        className={`py-1 px-2 rounded-full w-fit font-pop ${getStatusStyles(
                          job.status
                        )}`}
                      >
                        {job.status}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className={`radial-progress before:bg-gray-400 text-sm font-semibold ${
                          getStatusStyles(job.status).split(" ")[1]
                        } font-pop`}
                        style={{
                          "--value": getProgressPercentage(job.status),
                          "--size": "3rem",
                        }}
                        role="progressbar"
                      >
                        {getProgressPercentage(job.status)}%
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="basis-[35%] bg-white/50 py-4 px-4 rounded-xl flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4 font-pop justify-between">
          <h2 className="text-xl font-medium">Overall Progress</h2>
          <select
            value={selectedPeriod}
            onChange={handlePeriodChange}
            className="bg-white outline-none border-none rounded-2xl py-2 px-4 font-pop"
          >
            <option value="">All</option>
            <option value="30days">Last 30 days</option>
            <option value="7days">Last 7 days</option>
            <option value="2days">Last 2 days</option>
            <option value="today">Today</option>
          </select>
        </div>
        <div className="w-full">
          <Gauge
            value={
              data?.completedPercentage
                ? parseFloat(data.completedPercentage)
                : 0
            }
            startAngle={-110}
            endAngle={110}
            height={200}
            sx={{
              [`& .${gaugeClasses.valueText}`]: {
                fontSize: 30,
                transform: "translate(0px, 0px)",
              },
            }}
            className="w-full font-pop"
            text={({ value }) => `${data?.completed || 0} Completed`}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 justify-between">
          <div className="flex flex-col gap-2 font-pop">
            <h2 className="text-lg text-black font-medium">
              {loading ? "Loading..." : data?.totalProjects || 0}
            </h2>
            <p className="text-gray-500">Total Projects</p>
          </div>
          <div className="flex flex-col gap-2 font-pop">
            <h2 className="text-lg text-primarygreen font-medium">
              {loading ? "Loading..." : data?.completed || 0}
            </h2>
            <p className="text-gray-500">Completed</p>
          </div>
          <div className="flex flex-col gap-2 font-pop">
            <h2 className="text-lg text-yellow-500 font-medium">
              {loading ? "Loading..." : data?.delayed || 0}
            </h2>
            <p className="text-gray-500">Delayed</p>
          </div>
          <div className="flex flex-col gap-2 font-pop">
            <h2 className="text-lg text-red-500 font-medium">
              {loading ? "Loading..." : data?.ongoing || 0}
            </h2>
            <p className="text-gray-500">On Going</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessSummary;
